import React from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useFetchQuestionsQuery } from '../../../../../../../store/apis/question.api'
import { buildAssignmentPath } from '../../../../../../../utils/link.helper'
import { Alert, Card, Spin } from 'antd'
import Title from 'antd/es/typography/Title'
import Text from 'antd/es/typography/Text'
import { primaryColor, successColor } from '../../../../../../../constants/styles'
import { truncate } from 'lodash'
import { useVersionId } from '../../../../../../../utils/parseURL.helper'
import { useNavigate } from 'react-router-dom'
import { useFetchQuestionsApproveAnalyticsQuery } from '../../../../../../../store/apis/analytics.api'
import { flatten } from '../../../../../../../utils/array.helper'

const BAR_HEIGHT = 36

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload
        return (
            <Card size='small'>
                <Title level={5} style={{ margin: 0, fontSize: '0.9rem' }}>
                    {data.question}
                </Title>
                <Text>{`${data.percent}% - ${data.number} submissions`}</Text>
            </Card>
        )
    }
    return null
}

const CustomBar = props => {
    const { x, y, width, height, value } = props
    const fill = value === 100 ? successColor : primaryColor

    return <rect x={x} y={y} width={width} height={Math.min(height, 48)} fill={fill} cursor='pointer' rx={4} ry={4} />
}

const HumanReviewProgressChart = ({ token }) => {
    const navigate = useNavigate()
    const versionId = useVersionId()

    const { data: analyticsPerQuestion } = useFetchQuestionsApproveAnalyticsQuery({ token })
    let { data: questions, isFetching, error } = useFetchQuestionsQuery({ token, versionId })
    questions = flatten(questions, { excludeParents: true })

    const data =
        analyticsPerQuestion && questions
            ? questions.map(question => ({
                  id: question.id,
                  question: question.name || question.content,
                  percent: analyticsPerQuestion[question.id].reviewedByHuman,
                  number: Math.round(
                      (analyticsPerQuestion[question.id].totalSubmissions *
                          analyticsPerQuestion[question.id].reviewedByHuman) /
                          100,
                  ),
              }))
            : []

    const handleBarClick = entry => {
        if (entry && entry.id) {
            navigate(buildAssignmentPath(token, entry.id))
        }
    }

    const chartHeight = Math.max(250, data.length * BAR_HEIGHT + 100)

    if (error) return <Alert message={error} type='error' showIcon />

    return (
        <Spin spinning={isFetching} tip='Loading...'>
            <ResponsiveContainer width='100%' height={chartHeight}>
                <BarChart data={data} layout='vertical' margin={{ top: 0, right: 12, left: 12, bottom: 36 }}>
                    <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                    <XAxis
                        type='number'
                        domain={[0, 100]}
                        tickFormatter={value => `${value}%`}
                        label={{ value: 'Percent of Submissions', position: 'bottom', offset: 15 }}
                    />
                    <YAxis
                        dataKey='question'
                        type='category'
                        tick={{ fontSize: 12 }}
                        width={144}
                        tickFormatter={value => truncate(value)}
                        label={{ value: 'Questions', angle: -90, position: 'left' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey='percent' shape={<CustomBar />} onClick={data => handleBarClick(data)} />
                </BarChart>
            </ResponsiveContainer>
        </Spin>
    )
}

export default HumanReviewProgressChart
